/**
 * ``useCaseSources`` — case-scoped Faits + Pièces for the Sources tab.
 *
 * - **Faits** come from `GET /cases/{id}/facts` (CaseFact: text/status/
 *   category) — display-only (a fait is not an authority; cross-ref is
 *   deferred, plan §13 D2).
 * - **Pièces** come from the BE reference directory `GET /cases/{id}/refs`
 *   (F1 PR-4) — each carries a BE-assigned (party, ordinal, token), so they
 *   are **citable** (« Pièce n° N »). This fixes the PR-C conflation that
 *   listed raw uploaded files (`/documents`) instead of bordereau pièces.
 *
 * Both gated on an active case + filtered client-side by the search query.
 * W1: pure orchestration — fetch + a trivial substring filter, no legal
 * logic. No Zustand store (reuses useCaseStore for the active context).
 */

import { useCallback, useMemo, useState } from 'react'
import { getContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import type { CaseFact } from '@/domain/case/entity'
import type { PieceRef } from '@/domain/case-refs/entity'

export interface UseCaseSourcesResult {
  faits: CaseFact[]
  pieces: PieceRef[]
  loading: boolean
  error: Error | null
  /** Fetch the active case's faits + pièces. No-op when no case is active. */
  load: () => Promise<void>
  /** Set the client-side filter query (shared with the corpus search box). */
  setQuery: (q: string) => void
}

function matches(haystack: string, q: string): boolean {
  return haystack.toLowerCase().includes(q.toLowerCase())
}

export function useCaseSources(): UseCaseSourcesResult {
  const [allFaits, setAllFaits] = useState<CaseFact[]>([])
  const [allPieces, setAllPieces] = useState<PieceRef[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const load = useCallback(async () => {
    const active = useCaseStore.getState().activeContext
    if (!active) return
    setLoading(true)
    setError(null)
    try {
      const { caseList, caseRefs } = getContainer()
      const [faits, directory] = await Promise.all([
        caseList.listFacts(active.caseId),
        caseRefs.resolve(active.caseId),
      ])
      setAllFaits(faits)
      setAllPieces(directory.pieces)
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[useCaseSources] load failed', wrapped)
      setError(wrapped)
    } finally {
      setLoading(false)
    }
  }, [])

  const faits = useMemo(
    () => (query.trim() ? allFaits.filter((f) => matches(f.text, query)) : allFaits),
    [allFaits, query],
  )
  const pieces = useMemo(
    () =>
      query.trim()
        ? allPieces.filter((p) => matches(p.label, query))
        : allPieces,
    [allPieces, query],
  )

  return { faits, pieces, loading, error, load, setQuery }
}
